import type { Website } from '@lensora/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Spinner,
} from '@lensora/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Globe, Plus, Trash2, ExternalLink, RotateCw } from 'lucide-react'
import * as React from 'react'

import { useMe } from '@/hooks/use-me'
import { api, ApiError } from '@/lib/api'

// ─── Data hooks ──────────────────────────────────────────────────

function useWebsites() {
  return useQuery({
    queryKey: ['websites'],
    queryFn: () => api.get<Website[]>('/api/websites'),
    // Poll while any website is mid-crawl so status badges (and the re-run
    // button) update without a manual refresh — mirrors the legacy settings
    // page's polling behaviour.
    refetchInterval: (query) => {
      const data = query.state.data as Website[] | undefined
      const crawling = data?.some((w) => w.crawlStatus === 'crawling' || w.crawlStatus === 'analyzing')
      return crawling ? 3000 : false
    },
  })
}

function useRerunCrawlMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/websites/${id}/crawl`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websites'] }),
  })
}

function useCreateWebsiteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; url: string }) =>
      api.post<Website>('/api/websites', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websites'] }),
  })
}

function useDeleteWebsiteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/websites/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websites'] }),
  })
}

// ─── Add website modal ───────────────────────────────────────────

function AddWebsiteModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = React.useState('')
  const [url, setUrl] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const mutation = useCreateWebsiteMutation()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await mutation.mutateAsync({ name, url })
      onCreated()
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.code === 'DUPLICATE_URL' ? 'A website with this URL already exists.' : err.message)
      } else {
        setError('Failed to add website. Please try again.')
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-lg">
        <h2 className="mb-4 text-lg font-semibold text-text">Add website</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="website-name">Website name</Label>
            <Input
              id="website-name"
              placeholder="My Company Blog"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="website-url">Website URL</Label>
            <Input
              id="website-url"
              type="url"
              placeholder="https://example.com"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-feedback-error">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Adding…' : 'Add website'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Delete confirmation ─────────────────────────────────────────

function DeleteConfirmModal({
  website,
  onClose,
  onConfirm,
  isDeleting,
}: {
  website: Website
  onClose: () => void
  onConfirm: () => void
  isDeleting: boolean
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-lg">
        <h2 className="mb-2 text-lg font-semibold text-text">Remove website?</h2>
        <p className="mb-4 text-sm text-text-muted">
          <strong>{website.name}</strong> and all associated analysis data will be permanently removed.
          This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isDeleting}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? 'Removing…' : 'Remove website'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Website card ────────────────────────────────────────────────

function WebsiteCard({
  website,
  onDelete,
  onRerunCrawl,
  isRerunning,
}: {
  website: Website
  onDelete: (website: Website) => void
  onRerunCrawl: (website: Website) => void
  isRerunning: boolean
}) {
  const statusVariant: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
    active: 'default',
    pending: 'secondary',
    crawling: 'secondary',
    failed: 'destructive',
  }

  const isCrawling = website.crawlStatus === 'crawling' || website.crawlStatus === 'analyzing'

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Globe className="h-4 w-4 shrink-0 text-text-muted" />
            <CardTitle className="truncate text-base">{website.name}</CardTitle>
          </div>
          <Badge variant={statusVariant[website.crawlStatus] ?? 'outline'} className="shrink-0 capitalize">
            {website.crawlStatus}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <a
          href={website.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-text-muted hover:text-brand-primary truncate"
        >
          <ExternalLink className="h-3 w-3 shrink-0" />
          <span className="truncate">{website.url}</span>
        </a>
        <div className="mt-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            className="text-text-muted hover:text-brand-primary"
            disabled={isCrawling || isRerunning}
            onClick={() => onRerunCrawl(website)}
          >
            <RotateCw className={`mr-1.5 h-3.5 w-3.5 ${isCrawling || isRerunning ? 'animate-spin' : ''}`} />
            {isCrawling ? 'Crawling…' : 'Re-run audit'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-feedback-error hover:text-feedback-error hover:bg-feedback-error/10"
            onClick={() => onDelete(website)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Page ────────────────────────────────────────────────────────

export function WebsitesPage() {
  const { data: me } = useMe()
  const { data: websites, isLoading } = useWebsites()
  const deleteMutation = useDeleteWebsiteMutation()
  const rerunMutation = useRerunCrawlMutation()
  const [showAdd, setShowAdd] = React.useState(false)
  const [toDelete, setToDelete] = React.useState<Website | null>(null)
  const [rerunError, setRerunError] = React.useState<string | null>(null)

  async function handleRerunCrawl(website: Website) {
    setRerunError(null)
    try {
      await rerunMutation.mutateAsync(website.id)
    } catch (err) {
      if (err instanceof ApiError) {
        const messages: Record<string, string> = {
          CRAWL_IN_PROGRESS: 'A crawl is already in progress for this website.',
          NOT_CONFIGURED: 'Crawling is not configured for this environment.',
          INSUFFICIENT_CREDITS: 'Not enough credits to re-run this audit.',
        }
        setRerunError(messages[err.code ?? ''] ?? err.message)
      } else {
        setRerunError('Failed to start crawl. Please try again.')
      }
    }
  }

  const isClient = me?.user.role === 'client'
  const title = isClient ? 'My Websites' : 'Websites'

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text">{title}</h2>
          <p className="mt-1 text-sm text-text-muted">
            {isClient
              ? 'Manage the websites you want to track.'
              : 'Track and manage websites across all clients.'}
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add website
        </Button>
      </div>

      {rerunError && (
        <p className="rounded-lg border border-feedback-error/30 bg-feedback-error/10 px-4 py-2 text-sm text-feedback-error">
          {rerunError}
        </p>
      )}

      {(!websites || websites.length === 0) ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Globe className="mb-4 h-12 w-12 text-text-muted opacity-40" />
            <p className="text-text font-medium">No websites yet</p>
            <p className="mt-1 text-sm text-text-muted">
              Add your first website to start tracking its AI visibility.
            </p>
            <Button className="mt-4" onClick={() => setShowAdd(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add website
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {websites.map((w) => (
            <WebsiteCard
              key={w.id}
              website={w}
              onDelete={setToDelete}
              onRerunCrawl={handleRerunCrawl}
              isRerunning={rerunMutation.isPending && rerunMutation.variables === w.id}
            />
          ))}
        </div>
      )}

      {showAdd && (
        <AddWebsiteModal
          onClose={() => setShowAdd(false)}
          onCreated={() => setShowAdd(false)}
        />
      )}

      {toDelete && (
        <DeleteConfirmModal
          website={toDelete}
          onClose={() => setToDelete(null)}
          isDeleting={deleteMutation.isPending}
          onConfirm={async () => {
            await deleteMutation.mutateAsync(toDelete.id)
            setToDelete(null)
          }}
        />
      )}
    </div>
  )
}
